import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  query,
  collection,
  where,
  getDocs,
} from "firebase/firestore";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../service/firebase";
import {
  removeTurmaMember,
  updateTurmaMemberStatus,
  updateTurmaMemberAdmin,
} from "../../../service/turmaService";
import "./TurmaDetalhe.css";

const TURMA_STATUS = {
  0: { label: "Inativa", className: "inativa" },
  1: { label: "Ativa", className: "ativa" },
  2: { label: "Encerrada", className: "encerrada" },
};

const getTurmaStatusInfo = (status) =>
  TURMA_STATUS.hasOwnProperty(status) ? TURMA_STATUS[status] : TURMA_STATUS[1];

const getPositionAbbreviation = (position) => {
  const abbreviations = {
    "Goleiro": "GOL",
    "Zagueiro": "ZAG",
    "Lateral": "LAT",
    "Volante": "VOL",
    "Meia": "MEI",
    "Atacante": "ATA",
    "Pivô": "PIV",
    "Fixo": "FIX",
    "Ala": "ALA"
  };
  return abbreviations[position] || position;
};

const TurmaDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [turma, setTurma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [jogosRecentes, setJogosRecentes] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [integrantes, setIntegrantes] = useState([]);
  const [integrantesLoading, setIntegrantesLoading] = useState(true);
  const [integrantesError, setIntegrantesError] = useState("");
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [memberActionLoading, setMemberActionLoading] = useState(false);
  const [memberActionMessage, setMemberActionMessage] = useState("");
  const [memberActionError, setMemberActionError] = useState("");

  useEffect(() => {
    const handleGlobalClick = (event) => {
      if (!event.target.closest(".turma-member-actions")) {
        setMenuOpenFor(null);
      }
    };
    document.addEventListener("click", handleGlobalClick);
    return () => {
      document.removeEventListener("click", handleGlobalClick);
    };
  }, []);

  const getNomePreferencial = useCallback((usuario, fallback = "Jogador") => {
    if (!usuario) return fallback;
    return (
      usuario.nickname ||
      usuario.displayName ||
      usuario.nome ||
      usuario.email ||
      fallback
    );
  }, []);

  const fetchIntegrantes = useCallback(
    async (turmaId) => {
      setIntegrantesLoading(true);
      setIntegrantesError("");
      try {
        const membrosQuery = query(
          collection(db, "jogador_turma"),
          where("id_turma", "==", turmaId)
        );
        const membrosSnapshot = await getDocs(membrosQuery);
        const membrosComUsuario = await Promise.all(
          membrosSnapshot.docs.map(async (docSnapshot) => {
            const membroData = docSnapshot.data();
            try {
              const usuarioDoc = await getDoc(
                doc(db, "usuarios", membroData.id_usuario)
              );
              const usuarioData = usuarioDoc.exists()
                ? usuarioDoc.data()
                : null;
              return {
                id: docSnapshot.id,
                ...membroData,
                usuario: usuarioData,
              };
            } catch (usuarioErr) {
              console.error("Erro ao carregar usuário da turma", usuarioErr);
              return {
                id: docSnapshot.id,
                ...membroData,
                usuario: null,
              };
            }
          })
        );

        const ordenados = membrosComUsuario.sort((a, b) => {
          const nomeA = getNomePreferencial(a.usuario, "");
          const nomeB = getNomePreferencial(b.usuario, "");
          return nomeA.localeCompare(nomeB);
        });

        setIntegrantes(ordenados);
      } catch (err) {
        console.error("Erro ao carregar integrantes da turma", err);
        setIntegrantesError(
          "Não foi possível carregar os integrantes desta turma."
        );
      } finally {
        setIntegrantesLoading(false);
      }
    },
    [getNomePreferencial]
  );

  const podeRetomarJogo = (jogo) =>
    Boolean(
      isAdmin &&
        user?.uid &&
        jogo?.id_usuario_criador === user.uid &&
        jogo?.status === 1
    );

  const handleRetomarJogo = (jogo) => {
    if (!podeRetomarJogo(jogo)) return;
    navigate(`/turmas/${id}/jogos/${jogo.id}/andamento`);
  };

  const handleAbrirRelatorio = (jogo) => {
    navigate(`/turmas/${id}/jogos/${jogo.id}/relatorio/salvo`);
  };

  const fetchJogosDaTurma = async (turmaId) => {
    try {
      const jogosQuery = query(
        collection(db, "jogo_turma"),
        where("id_turma", "==", turmaId)
      );
      const jogosSnapshot = await getDocs(jogosQuery);
      const jogos = jogosSnapshot.docs
        .map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        }))
        .sort((a, b) => {
          const dataA = a.created_at?.toMillis?.() || 0;
          const dataB = b.created_at?.toMillis?.() || 0;
          return dataB - dataA;
        });
      setJogosRecentes(jogos);
    } catch (err) {
      console.error("Erro ao carregar jogos da turma", err);
      setJogosRecentes([]);
    }
  };

  const handleToggleMemberMenu = (event, membroId) => {
    event.stopPropagation();
    setMenuOpenFor((prev) => (prev === membroId ? null : membroId));
  };

  const handleMemberAction = async (event, integrante, action) => {
    event.stopPropagation();
    if (integrante.id_usuario === user?.uid) {
      return;
    }
    setMenuOpenFor(null);
    setMemberActionError("");
    setMemberActionMessage("");
    setMemberActionLoading(true);
    const nome = getNomePreferencial(integrante.usuario, "O jogador");
    try {
      if (action === "inativar") {
        await updateTurmaMemberStatus({
          membershipId: integrante.id,
          status: 0,
        });
        setMemberActionMessage(`${nome} foi inativado.`);
      } else if (action === "ativar") {
        await updateTurmaMemberStatus({
          membershipId: integrante.id,
          status: 1,
        });
        setMemberActionMessage(`${nome} foi ativado.`);
      } else if (action === "remover") {
        await removeTurmaMember(integrante.id);
        setMemberActionMessage(`${nome} foi removido da turma.`);
      }
      await fetchIntegrantes(id);
    } catch (err) {
      console.error("Erro ao atualizar integrante", err);
      setMemberActionError(
        err.message || "Não foi possível atualizar o integrante."
      );
    } finally {
      setMemberActionLoading(false);
    }
  };

  const handleToggleAdmin = async (event, integrante, nextValue) => {
    event.stopPropagation();
    if (integrante.id_usuario === user?.uid) {
      return;
    }
    setMemberActionError("");
    setMemberActionMessage("");
    setMemberActionLoading(true);
    const nome = getNomePreferencial(integrante.usuario, "O jogador");
    try {
      await updateTurmaMemberAdmin({
        membershipId: integrante.id,
        admin: nextValue,
      });
      setMemberActionMessage(
        nextValue
          ? `${nome} agora é administrador da turma.`
          : `${nome} não é mais administrador da turma.`
      );
      await fetchIntegrantes(id);
    } catch (err) {
      console.error("Erro ao atualizar privilégios de admin", err);
      setMemberActionError("Não foi possível atualizar os privilégios agora.");
    } finally {
      setMemberActionLoading(false);
    }
  };

  const getJogoStatusLabel = (status) => {
    switch (status) {
      case 0:
        return "Não iniciado";
      case 1:
        return "Em andamento";
      case 2:
        return "Encerrado";
      case 3:
        return "Cancelado";
      default:
        return "Desconhecido";
    }
  };

  const formatJogoData = (timestamp) => {
    if (!timestamp) return "";
    if (typeof timestamp.toDate === "function") {
      return timestamp.toDate().toLocaleString("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      });
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      });
    }
    return "";
  };

  useEffect(() => {
    const loadData = async () => {
      setError("");
      if (!user) {
        setLoading(false);
        return;
      }
      setIntegrantes([]);
      setIntegrantesError("");
      setIntegrantesLoading(true);
      try {
        const turmaDoc = await getDoc(doc(db, "turmas", id));
        if (!turmaDoc.exists()) {
          setError("Turma não encontrada.");
          setLoading(false);
          return;
        }
        setTurma(turmaDoc.data());

        const membershipQuery = query(
          collection(db, "jogador_turma"),
          where("id_turma", "==", id),
          where("id_usuario", "==", user.uid)
        );
        const membershipSnapshot = await getDocs(membershipQuery);
        if (!membershipSnapshot.empty) {
          const membershipData = membershipSnapshot.docs[0].data();
          setIsAdmin(Boolean(membershipData.admin));
        }

        await fetchIntegrantes(id);
        await fetchJogosDaTurma(id);
      } catch (err) {
        console.error("Erro ao carregar detalhes da turma", err);
        setError("Não foi possível carregar os dados da turma agora.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, user, fetchIntegrantes]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="body">
          <div className="center">
            <div className="body-content card turma-detalhe-card">
              <span>Carregando turma...</span>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="body">
          <div className="center">
            <div className="body-content card turma-detalhe-card">
              <p className="turma-error" role="alert">
                {error}
              </p>
              <button
                className="btn-default"
                type="button"
                onClick={() => navigate(-1)}
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!turma) {
    return null;
  }

  const turmaEstado = typeof turma.status === "number" ? turma.status : 1;
  const statusInfo = getTurmaStatusInfo(turmaEstado);
  const turmaAtiva = turmaEstado === 1;

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card turma-detalhe-card">
            <div className="topo-page turma-header">
              <strong># {turma.nome}</strong>
              <span className={`turma-status-chip ${statusInfo.className}`}>
                {statusInfo.label}
              </span>
            </div>

            <button
              type="button"
              className="turma-toggle-info"
              onClick={() => setShowInfo((prev) => !prev)}
            >
              <span
                className={`toggle-icon ${showInfo ? "open" : ""}`}
                aria-hidden="true"
              >
                ▶
              </span>
              {showInfo ? "Esconder informações" : "Exibir informações"}
            </button>

            {showInfo && (
              <>
                <div className="turma-info-grid">
                  <div>
                    <span className="label">Descrição</span>
                    <strong>{turma.descricao}</strong>
                  </div>
                  <div>
                    <span className="label">Local</span>
                    <strong>{turma.local}</strong>
                  </div>
                  <div>
                    <span className="label">Dias da semana</span>
                    <strong>
                      {turma.dias_semana?.join(", ") || "Não informado"}
                    </strong>
                  </div>
                  <div>
                    <span className="label">Jogadores por time</span>
                    <strong>{turma.qtd_jogadores_time}</strong>
                  </div>
                  <div>
                    <span className="label">Tempo de partida</span>
                    <strong>{turma.tempo_de_partida} min</strong>
                  </div>
                  <div>
                    <span className="label">Máx. pontos</span>
                    <strong>{turma.max_pontos_partida}</strong>
                  </div>
                  <div>
                    <span className="label">Dados individuais</span>
                    <strong>{turma.dados_individuais ? "Sim" : "Não"}</strong>
                  </div>
                </div>
                {isAdmin && (
                  <Link
                    to={`/turmas/${id}/configuracoes`}
                    className="turma-config-btn"
                  >
                    Configurações da turma
                  </Link>
                )}

                <div className="turma-section">
                  <h3>Regras</h3>
                  <p>{turma.regras}</p>
                </div>
              </>
            )}

            <div className="turma-section">
              <h3>Últimos jogos</h3>
              {jogosRecentes.length === 0 ? (
                <div className="turmas-placeholder">
                  <span>Ainda não registramos jogos nesta turma.</span>
                </div>
              ) : (
                <ul className="jogos-list">
                  {jogosRecentes.map((jogo) => {
                    const statusLabel = getJogoStatusLabel(jogo.status);
                    const data = formatJogoData(jogo.created_at);
                    const podeRetomar = podeRetomarJogo(jogo);
                    const podeVerRelatorio =
                      jogo.status === 2 && jogo.id_turma === id;
                    return (
                      <li
                        key={jogo.id}
                        className={podeRetomar ? "jogo-item-clickable" : ""}
                      >
                        <div>
                          <strong>{jogo.nome || "Jogo da turma"}</strong>
                          <div>
                            <small>Status: {statusLabel}</small>
                            {data && (
                              <>
                                {" "}
                                <small>• {data}</small>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="jogos-list-actions">
                          {podeRetomar && (
                            <button
                              type="button"
                              className="btn-default acessar-jogo-btn"
                              onClick={() => handleRetomarJogo(jogo)}
                            >
                              Retomar
                            </button>
                          )}
                          {podeVerRelatorio && (
                            <button
                              type="button"
                              className="btn-outline acessar-jogo-btn"
                              onClick={() => handleAbrirRelatorio(jogo)}
                            >
                              Ver relatório
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="turma-actions">
                {isAdmin && turmaAtiva && (
                  <Link
                    to={`/turmas/${id}/jogos/novo`}
                    className="btn-default novo-jogo-btn"
                  >
                    Novo Jogo
                  </Link>
                )}
                {jogosRecentes.length > 0 && (
                  <Link
                    to={`/turmas/${id}/estatisticas`}
                    className="btn-estatisticas"
                  >
                    📊 Ver Estatísticas
                  </Link>
                )}
              </div>
              {isAdmin && !turmaAtiva && (
                <p className="turma-warning">
                  Esta turma está {statusInfo.label.toLowerCase()} e não pode
                  iniciar novos jogos.
                </p>
              )}
            </div>

            <div className="turma-section">
              <h3>Integrantes da turma</h3>
              {memberActionError && (
                <p className="turma-error" role="alert">
                  {memberActionError}
                </p>
              )}
              {memberActionMessage && (
                <p className="turma-success">{memberActionMessage}</p>
              )}
              {integrantesError && (
                <p className="turma-error" role="alert">
                  {integrantesError}
                </p>
              )}
              {integrantesLoading ? (
                <span>Carregando integrantes...</span>
              ) : integrantes.length === 0 ? (
                <div className="turma-integrantes-empty">
                  <span>Ainda não há jogadores cadastrados nesta turma.</span>
                </div>
              ) : (
                <ul className="turma-members-list">
                  {integrantes.map((integrante) => {
                    const nome = getNomePreferencial(
                      integrante.usuario,
                      "Jogador sem nome"
                    );
                    const statusValue =
                      integrante.status === 0 || integrante.status === 1
                        ? integrante.status
                        : 1;
                    const statusLabel = statusValue === 0 ? "Inativo" : "Ativo";
                    const badgeClass = statusValue === 0 ? "inativo" : "ativo";
                    const adminSuffix = integrante.admin ? " • Admin" : "";
                    const isCurrentUser = integrante.id_usuario === user?.uid;
                    const actionLabel =
                      statusValue === 0 ? "Ativar" : "Inativar";
                    const nextAction =
                      statusValue === 0 ? "ativar" : "inativar";
                    return (
                      <li key={integrante.id} className="turma-member-item">
                        <div className="turma-member-info">
                          <strong>{nome}</strong>
                          {(integrante.usuario?.posicao ||
                            integrante.usuario?.overall) && (
                            <small className="turma-member-stats">
                              {integrante.usuario?.posicao && (
                                <span className="turma-member-chip posicao">
                                  {getPositionAbbreviation(
                                    integrante.usuario.posicao
                                  )}
                                </span>
                              )}
                              {integrante.usuario?.overall && (
                                <span className="turma-member-chip overall">
                                  {integrante.usuario.overall}
                                </span>
                              )}
                            </small>
                          )}
                        </div>
                        <div className="turma-member-actions">
                          <span className={`turma-member-status ${badgeClass}`}>
                            {statusLabel}
                            {adminSuffix}
                          </span>
                          {isAdmin && (
                            <div className="turma-member-menu-wrapper">
                              <button
                                type="button"
                                className="turma-member-menu-btn"
                                aria-haspopup="true"
                                aria-expanded={menuOpenFor === integrante.id}
                                disabled={memberActionLoading}
                                onClick={(event) =>
                                  handleToggleMemberMenu(event, integrante.id)
                                }
                              >
                                ⋮
                              </button>
                              {menuOpenFor === integrante.id && (
                                <div className="turma-member-menu">
                                  {!isCurrentUser && (
                                    <div className="turma-member-menu-admin">
                                      <div>
                                        <span>Administrador</span>
                                        <small>Permissões totais</small>
                                      </div>
                                      <label className="turma-admin-toggle">
                                        <input
                                          type="checkbox"
                                          disabled={memberActionLoading}
                                          checked={Boolean(integrante.admin)}
                                          onChange={(event) =>
                                            handleToggleAdmin(
                                              event,
                                              integrante,
                                              event.target.checked
                                            )
                                          }
                                        />
                                        <span className="slider" />
                                      </label>
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    disabled={
                                      isCurrentUser || memberActionLoading
                                    }
                                    className={isCurrentUser ? "disabled" : ""}
                                    onClick={(event) =>
                                      handleMemberAction(
                                        event,
                                        integrante,
                                        nextAction
                                      )
                                    }
                                  >
                                    {actionLabel}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={
                                      isCurrentUser || memberActionLoading
                                    }
                                    className={`danger ${
                                      isCurrentUser ? "disabled" : ""
                                    }`.trim()}
                                    onClick={(event) =>
                                      handleMemberAction(
                                        event,
                                        integrante,
                                        "remover"
                                      )
                                    }
                                  >
                                    Remover
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {isAdmin && turmaAtiva && (
              <Link to={`/turmas/${id}/convites`} className="btn-default">
                Convidar jogadores
              </Link>
            )}
            {isAdmin && !turmaAtiva && (
              <p className="turma-warning">
                Ative a turma nas configurações para convidar novos jogadores.
              </p>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TurmaDetalhe;
